import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { markPlansGenerated, usePlansStatus } from '../Plans/plansGenerated';
import { hasCurrentYearReport } from '../ProgramEvaluation/program-evaluation-hook';
import { loadAccessRoster, loadPlanRoster } from '../Upload/studentRoster';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type GenerateState = { count?: number } | null;

type OutputCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
  onClick: () => void;
};

function OutputCard({ icon, title, description, action, onClick }: OutputCardProps) {
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 2,
        border: '1px solid #E4E4EC',
        bgcolor: '#FFFFFF',
        p: 3,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ color: '#1F1F70' }}>{icon}</Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0F1024' }}>{title}</Typography>
      <Typography sx={{ fontSize: 14, color: '#5B5B73', mb: 1 }}>{description}</Typography>
      <Button
        variant="contained"
        onClick={onClick}
        sx={{
          bgcolor: '#1F1F70',
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1,
          borderRadius: 1.5,
          '&:hover': { bgcolor: '#18185A' },
        }}
      >
        {action}
      </Button>
    </Box>
  );
}

export function GenerateChoice() {
  const altOnlyRoster = loadAccessRoster().length === 0 && loadPlanRoster().length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as GenerateState;
  const count = state?.count;
  const plansStatus = usePlansStatus();
  const plansGenerated = plansStatus?.generated === true;
  const [reportExists, setReportExists] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void hasCurrentYearReport(controller.signal).then((exists) => {
      if (!controller.signal.aborted) setReportExists(exists);
    });
    return () => controller.abort();
  }, []);

  const chooseProgramEvaluation = () => {
    navigate('/program-evaluation', { state: { generate: true } });
  };

  const chooseStudentActionPlans = () => {
    markPlansGenerated().catch(() => {});
    navigate('/plans', count != null ? { state: { count } } : undefined);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: '#F6F6F8' }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
      <Box sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            maxWidth: 880,
            mx: 'auto',
            mt: { xs: 1, md: 2 },
            borderRadius: 2,
            border: '1px solid #E4E4EC',
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F1024', mb: 0.5 }}>
            Generate Reports &amp; Plans
          </Typography>
          <Typography sx={{ color: '#5B5B73', fontSize: 14, mb: 3 }}>
            Choose a report or plan to generate. You can always generate the other(s) later.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {!altOnlyRoster && (
            <OutputCard
              icon={<BarChartIcon />}
              title="Program Evaluation"
              description="See how your EL program performed this past year compared to previous years."
              action={reportExists ? 'Regenerate Report' : 'Generate Report'}
              onClick={chooseProgramEvaluation}
            />
            )}
            <OutputCard
              icon={<AssignmentIndIcon />}
              title="Student Action Plans"
              description="See each students YoY growth, their progress, and recommended supports."
              action={plansGenerated ? 'Regenerate Plans' : 'Generate Plans'}
              onClick={chooseStudentActionPlans}
            />
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
